/* eslint-disable @typescript-eslint/no-use-before-define */
import { type ReactNode } from 'react';
import * as React from 'react';
import { rgba } from 'polished';
import styled, { css } from 'styled-components';

import { KnowledgeIcon } from '../../icons/Icons';

const Wrapper = styled.div<{ $dark: boolean; $loud: boolean }>`
  display: flex;
  position: relative;
  margin: 0 0 40px;

  ${({ $dark, $loud }) =>
    $dark &&
    css`
      && {
        ${Inner} {
          color: ${({ theme }) => theme.fontColor.DEFAULT};
          background-color: ${({ theme }) => rgba(theme.highlightColor, 0.07)};
          border-left: 1px solid ${({ theme }) => theme.highlightColor};

          ${$loud &&
          css`
            color: ${({ theme }) => theme.colors.WHITE};
            border: 1px solid ${({ theme }) => theme.highlightColor};
          `}
        }

        ${Icon} {
          background-color: ${({ theme }) => theme.colors.BLACK};
          border: 1px solid ${({ theme }) => theme.highlightColor};

          svg {
            color: ${({ theme }) => theme.highlightColor};
          }
        }

        a {
          color: ${({ theme }) => theme.highlightColor} !important;
        }
      }
    `};
`;

const Inner = styled.div<{ $small: boolean }>`
  background-color: ${({ theme }) => rgba(theme.colors.LIGHT_PURPLE, 0.07)};
  padding: 25px 35px;
  border-left: 1px solid ${({ theme }) => theme.colors.LIGHT_PURPLE};
  color: ${({ theme }) => theme.colors.PURPLE};
  font-size: ${({ theme }) => theme.fontSize.MEDIUM};
  line-height: ${({ theme }) => theme.lineHeight.LARGE};

  *:last-child {
    margin-bottom: 0 !important;
  }

  p,
  ul,
  li {
    font-size: ${({ theme }) => theme.fontSize.MEDIUM} !important;
    line-height: ${({ theme }) => theme.lineHeight.LARGE} !important;
    margin-bottom: 20px;
  }

  code {
    background-color: ${({ theme }) =>
      rgba(theme.colors.LIGHT_PURPLE, 0.15)} !important;
    color: ${({ theme }) => theme.colors.PURPLE} !important;
    font-size: ${({ theme }) => theme.fontSize.SMALL} !important;
  }

  a {
    color: ${({ theme }) => theme.colors.PURPLE} !important;
  }

  ${({ $small }) =>
    $small &&
    css`
      font-size: ${({ theme }) => theme.fontSize.DEFAULT};
      line-height: ${({ theme }) => theme.lineHeight.DEFAULT};

      p,
      ul,
      li {
        font-size: ${({ theme }) => theme.fontSize.DEFAULT} !important;
        line-height: ${({ theme }) => theme.lineHeight.DEFAULT} !important;
      }
    `};
`;

const Icon = styled.div`
  position: absolute;
  top: 21px;
  left: -18px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 35px;
  height: 35px;
  background-color: ${({ theme }) => theme.colors.WHITE};
  border-radius: 50%;
  border: 1px solid ${({ theme }) => theme.borderColor.DEFAULT};
  box-shadow: 0 0 0 2px ${({ theme }) => theme.colors.WHITE} 0 0 0 3px
    ${({ theme }) => theme.colors.LIGHT_PURPLE};

  svg {
    color: ${({ theme }) => theme.colors.PURPLE};
  }
`;

interface CalloutProps {
  children: ReactNode;
  className?: string;
  dark?: boolean;
  icon?: ReactNode;
  loud?: boolean;
  small?: boolean;
  text?: string;
}

export const Callout: React.ComponentType<CalloutProps> = React.memo(
  ({
    children,
    className,
    dark = false,
    loud = false,
    icon,
    small = false
  }) => {
    return (
      <Wrapper $dark={dark} $loud={loud} className={className}>
        <Inner $small={small}>
          <Icon>{icon || <KnowledgeIcon />}</Icon>
          {children}
        </Inner>
      </Wrapper>
    );
  }
);
